import { PlayerStatus } from '@tunein/web-player-manager';
import classNames from 'clsx';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { mintSingleton } from 'src/client/mint';
import { player } from 'src/client/player';
import { FavoriteControl } from 'src/common/components/shared/controls/favorite/FavoriteControl';
import PlayErrorIcon from 'src/common/components/shared/svgIcons/player/PlayErrorIcon';
import Text from 'src/common/components/Text';
import { WEB_OPEN_INSTALL_APP_DIALOG_SHOW_PLAYER } from 'src/common/constants/experiments/dialog';
import * as localization from 'src/common/constants/localizations/player';
import { AD_BLOCKER_ERROR_MESSAGE_ENABLED } from 'src/common/constants/localizations/player';
import useActions from 'src/common/hooks/useActions';
import useExperiment from 'src/common/hooks/useExperiment';
import usePrevious from 'src/common/hooks/usePrevious';
import { useStores } from 'src/common/providers';
import cssVars from 'src/common/styles/variables';
import cssVariables from 'src/common/styles/variables';
import { getBoostNextPlayButtonWidth } from 'src/common/utils/boost';
import { isMedium, isSmall } from 'src/common/utils/breakpoints';
import { isDiscordMode } from 'src/common/utils/discord';
import { sharingFromPlayer } from '../../actions/share';
import {
  selectBottomAdBannerHeight,
  selectDiscordState,
  selectIsDiscord,
  selectIsFord,
  selectIsMobile,
} from '../../selectors/app';
import { selectIsDesktopNowPlayingEnabled } from '../../selectors/config';
import { selectIsUserSubscribed } from '../../selectors/me';
import { useShowAppToastInPlayer } from '../../utils/showAppToastInPlayer';
import MessagePopover from '../messagePopover/MessagePopover';
import FastForwardFifteenIcon from '../shared/svgIcons/FastForwardFifteenIcon';
import FastForwardThirtyIcon from '../shared/svgIcons/FastForwardThirtyIcon';
import BoostNextPlayIcon from '../shared/svgIcons/player/BoostNextPlayIcon';
import RewindFifteenIcon from '../shared/svgIcons/RewindFifteenIcon';
import { ActionDrawer } from './actionDrawer/ActionDrawer';
import PlayButton from './controls/playButton/PlayButton';
import Scrubber from './controls/scrubber/Scrubber';
import NowPlaying from './nowPlaying/NowPlaying';
import css from './player.module.scss';

export const PopOutMessage = () => (
  <div data-testid="playerPopOutMessage" className={css.informational}>
    <Text name={localization.POP_OUT_MESSAGE} />
  </div>
);

const playerErrorIconSize = isDiscordMode() ? '12px' : '16px';

export const ErrorMessage = ({ customMessage }) => (
  <div data-testid="playerErrorMessage" className={css.informational}>
    <div className={css.errorIcon}>
      <PlayErrorIcon width={playerErrorIconSize} height={playerErrorIconSize} />
    </div>
    <div className={css.errorMessageContainer}>
      {customMessage ? <Text name={customMessage} /> : <Text name={localization.ERROR_MESSAGE} />}
    </div>
  </div>
);

ErrorMessage.propTypes = {
  customMessage: PropTypes.string,
};

export function Player({
  breakpoint,
  disablePreroll,
  nextPlayButtonRef,

  // from Map View Player
  showFavoriteIcon,
  disableTitleScroll,
  disableScrubber,
  disableActionsContainer,
  playButtonContainerClassName,
  controlsClassName,
}) {
  const showPlayerOverDialog = useExperiment(WEB_OPEN_INSTALL_APP_DIALOG_SHOW_PLAYER);
  const isAdBlockerMessageEnabled = useExperiment(AD_BLOCKER_ERROR_MESSAGE_ENABLED);
  const actions = useActions({
    sharingFromPlayer,
  });
  const isMobile = useSelector(selectIsMobile);
  const isDiscord = useSelector(selectIsDiscord);
  const discordState = useSelector(selectDiscordState);
  const isFord = useSelector(selectIsFord);
  const isDesktopNowPlayingEnabled = useSelector(selectIsDesktopNowPlayingEnabled);
  const showAppToast = useShowAppToastInPlayer();
  const isUserSubscribed = useSelector(selectIsUserSubscribed);
  const bottomAdBannerHeight = useSelector(selectBottomAdBannerHeight);
  const { playerStore, appStore } = useStores();
  const guideItemPathname = appStore.guideItemPathname();
  const tunedGuideId = playerStore.currentGuideItem()?.playableGuideId;
  const metadata = playerStore.metadata() || {};
  const previousMetadata = usePrevious(metadata);
  const isVideoAdLoaded = playerStore.isVideoAdLoaded();
  const isLive = ((metadata?.isLive ?? true) && !metadata?.isLiveSeek) || isVideoAdLoaded;
  const nowPlayingRejectReason = metadata?.rejectReason;
  const isMediaAdLoaded = Boolean(metadata?.isAdBreak);
  const playerStatus = playerStore.playerStatus();
  const hasBoostAvailable = Boolean(metadata?.isBoostEnabled);
  const shouldAllowSwitch = Boolean(
    hasBoostAvailable &&
      playerStatus !== PlayerStatus.IDLE &&
      playerStatus !== PlayerStatus.LOADING,
  );
  const popout = playerStore.popout();

  let StatusMessage;
  let scrubberContent = !isLive || !isDiscord ? <Scrubber className={css.scrubber} /> : null;
  const isSmallBreakpoint = isSmall(breakpoint);
  const isMediumBreakpoint = isMedium(breakpoint);
  const showDoubleSeekButtonBreakpoint = isDiscord ? isSmallBreakpoint : isMediumBreakpoint;
  const boostNextPlayButtonWidth = getBoostNextPlayButtonWidth(isMediumBreakpoint);
  const FastForwardIcon = showDoubleSeekButtonBreakpoint
    ? FastForwardFifteenIcon
    : FastForwardThirtyIcon;

  if (playerStatus === PlayerStatus.FAILED && !popout?.showButton) {
    const errorMessage = (
      <div className={css.messageContainer}>
        <ErrorMessage
          customMessage={
            !isUserSubscribed &&
            isAdBlockerMessageEnabled &&
            mintSingleton.instance?.getState('audioVideoSdkLoadFailed')
              ? localization.AD_BLOCKER_ERROR_MESSAGE
              : ''
          }
        />
      </div>
    );
    scrubberContent = errorMessage;
    StatusMessage = errorMessage;
  }

  if (popout?.showButton) {
    scrubberContent = (
      <div className={css.messageContainer}>
        <PopOutMessage />
      </div>
    );
    StatusMessage = <PopOutMessage />;
  }

  useEffect(() => {
    if (
      !tunedGuideId ||
      !metadata ||
      metadata.guideId !== tunedGuideId ||
      metadata.guideId === previousMetadata?.guideId
    ) {
      return;
    }

    player.ready.then(() => {
      player.appReporter.reportBoostButtonVisibility('button', hasBoostAvailable);
      player.appReporter.reportBoostButtonVisibility('swipe', hasBoostAvailable);
    });
  }, [tunedGuideId, hasBoostAvailable, metadata, previousMetadata]);

  const handleBoostClick = (label) => {
    // isBoostLoaded will be true when already on boost
    const isSwitchingToBoost = !metadata.isBoostLoaded;
    player.playNext();
    // On playNext we set the listenIds used by reporting API, so reporting has to be called after
    player.appReporter.reportBoostSwitch(label, isSwitchingToBoost);
  };

  return (
    <div
      data-testid="player"
      className={classNames(css.playerContainer, {
        [css.withBoxShadow]: !bottomAdBannerHeight,
        [css.playerHighestIndex]: showPlayerOverDialog,
        [css.desktopPlayerContainer]: isDesktopNowPlayingEnabled,
        [css.noGuideId]: !tunedGuideId,
      })}
    >
      <div
        className={classNames(css.leftSection, {
          [css.hide]: isDiscord && !tunedGuideId,
        })}
      >
        {StatusMessage && <div className={css.leftErrorMessageContainer}>{StatusMessage}</div>}
        <div
          data-testid="nowPlayingContainer"
          data-guideid={tunedGuideId}
          className={classNames(css.nowPlayingContainer, {
            [css.hideSmall]: !!StatusMessage,
          })}
        >
          <NowPlaying
            isBoostFeaturedInPlayer={Boolean(metadata?.isBoostLoaded)}
            hasBoostAvailable={hasBoostAvailable}
            shouldAllowSwitch={shouldAllowSwitch}
            handleBoostClick={() => handleBoostClick('swipe')}
            nowPlaying={metadata}
            nowPlayingPathname={guideItemPathname}
            isMobile={isMobile}
            isMediaAdLoaded={isMediaAdLoaded}
            disableTitleScroll={disableTitleScroll}
          />
        </div>
      </div>
      <div
        className={classNames(css.controlsContainer, controlsClassName, {
          [css.hide]: isDiscord && !tunedGuideId,
          [css.isLive]: isLive,
          [css.failed]: playerStatus === PlayerStatus.FAILED,
        })}
      >
        {showAppToast && <MessagePopover className={css.playerMessage} />}
        <div className={css.controls}>
          {showFavoriteIcon && <FavoriteControl fill={cssVariables['--disabled-grey']} />}
          {hasBoostAvailable && (
            <div className={css.boostNextPlayIconContainer} ref={nextPlayButtonRef}>
              <BoostNextPlayIcon
                backgroundfill={
                  shouldAllowSwitch ? cssVars['--t-sharp'] : cssVars['--disabled-t-sharp']
                }
                width={`${boostNextPlayButtonWidth}px`}
                height={`${boostNextPlayButtonWidth}px`}
                onClick={() => handleBoostClick('button')}
              />
            </div>
          )}
          {!isLive && !isMediaAdLoaded && showDoubleSeekButtonBreakpoint && !isFord && (
            <RewindFifteenIcon
              onClick={() => player.seekBackward(15)}
              className={css.rewindContainer}
            />
          )}
          <div
            className={classNames(css.playButtonContainer, playButtonContainerClassName, {
              [css.isDisabled]:
                isDiscord &&
                playerStatus !== PlayerStatus.FAILED &&
                (!discordState?.canControlPlayback || nowPlayingRejectReason),
            })}
          >
            <PlayButton
              breakpoint={breakpoint}
              disablePreroll={disablePreroll}
              isLive={isLive}
              className={classNames(css.playButton, {
                [css.isLive]: isLive,
                [css.failed]: playerStatus === PlayerStatus.FAILED,
              })}
              enableDiscordControlTooltip={playerStatus !== PlayerStatus.FAILED}
            />
          </div>
          {!isLive && !isMediaAdLoaded && !isFord && (
            <FastForwardIcon
              onClick={() => player.seekForward(showDoubleSeekButtonBreakpoint ? 15 : 30)}
              className={css.fastForwardContainer}
            />
          )}
        </div>
        {!disableScrubber && !isFord && scrubberContent}
      </div>
      {!disableActionsContainer && (
        <ActionDrawer
          sharingFromPlayer={actions.sharingFromPlayer}
          tunedGuideId={tunedGuideId}
          isDiscord={isDiscord}
        />
      )}
    </div>
  );
}
